import { orientationsFromConfig, planesForLayoutSheet } from "../components/config/orientations";
import { saveOrientationShading, sceneShadingKey } from "../components/config/orientationEvidence";
import { mountingPlaneFor } from "./mounting";
import { resolveSceneModel } from "./sceneModel";
import { buildRows } from "./sceneRows";
import { buildSiteGeometry, checkRoofRows, combineTriangles, siteGeometryErrors } from "./siteGeometry";
import { buildProjectNeighbours, rowsMesh, sceneOffset, translateMesh } from "./projectScene";
import { loadMesh } from "./meshCache";
import { placeMesh } from "./stlLoader";
import { moduleQuad, moduleQuads, overlapCount, resolveRoofModuleOffsets } from "./moduleLayout";
import { detailedShadingEnabled, detailedSceneKey, detailedSceneInputs, topologyErrors } from "./detailedShading";
import { moduleSamples } from "./moduleShading";
import { buildShadingTableAsync } from "./shadingTable";
import { trackerRotation } from "./rowShading";
import { scenePreparationError } from "./scenePreparationIssue";

export function needsScenePreparation(cfg) {
  if (detailedShadingEnabled(cfg)) return true;
  if (cfg?.pvLosses?.dc?.shadingMode === "percentage") return false;
  // A dual-axis tracker has no layout shading (see sceneShadingPending).
  if (String(cfg?.mountType || "").toLowerCase() === "dual_axis") return false;
  return [cfg?.pv_design, ...(cfg?.orientations || []).slice(1)]
    .some((record) => record?.spacingInfo?.scene_review_required);
}

// Run preparation uses exactly the renderer's geometry and ray-casting kernel.
// Its immutable result belongs to this run; it never overwrites newer user edits.
export async function prepareSceneSimulation(cfg, { module, projectId, onProgress = () => {}, shouldCancel = () => false,
  measure = buildShadingTableAsync } = {}) {
  if (!needsScenePreparation(cfg)) return cfg;
  const cancelled = () => { if (shouldCancel()) throw new Error("Scene preparation cancelled."); };
  // planeIndex (1-based) says which surface is not ready, for callers that name it.
  const fail = (i, code = "layout_not_ready") => {
    const error = scenePreparationError(code, "Save and validate the physical layout before simulation.");
    if (Number.isInteger(i)) error.planeIndex = i + 1;
    throw error;
  };
  const chars = module?.characteristics || module || {};
  const length = Number(chars.Length ?? chars.length), width = Number(chars.Width ?? chars.width);
  if (!(length > 0 && width > 0)) throw scenePreparationError("module_dimensions", "Module dimensions are required to calculate scene shading.");
  if (detailedShadingEnabled(cfg)) {
    const errors = topologyErrors(cfg.pvDetailedShading.topology);
    if (errors.length) throw scenePreparationError("panel_topology", errors.join(" "), { validationErrors: errors });
    if (cfg.pvDetailedShading.topology.sourceModule !== cfg.read_module) throw scenePreparationError("module_changed", "Επιβεβαίωσε τη διάταξη κυψελών για το επιλεγμένο πάνελ.");
    if (cfg.mountType !== "fixed") throw scenePreparationError("tracking_unsupported", "Η πιλοτική λεπτομερής σκίαση υποστηρίζει σταθερή διάταξη.");
    if (cfg.pvLosses?.dc?.shadingMode === "percentage") throw scenePreparationError("percentage_mode", "Η λεπτομερής σκίαση χρειάζεται τη φυσική διάταξη.");
  }
  const planes = orientationsFromConfig(cfg), summaries = planesForLayoutSheet(cfg);
  const po = cfg.panelOrientation || {};
  const descriptors = planes.map((plane, i) => {
    const saved = plane.primary ? cfg.pv_design : plane;
    let spacing = saved?.spacingInfo;
    if (!spacing?.scene_review_required) fail(i);
    if (!spacing.module_mapping_valid) fail(i, "module_mapping");
    if (siteGeometryErrors(spacing.site_geometry).length) fail(i, "site_geometry");
    const roofPitch = plane.primary ? po.roofPitch ?? spacing.site_geometry?.roof?.pitchDeg
      : plane.roofPitch ?? spacing.site_geometry?.roof?.pitchDeg ?? po.roofPitch;
    const roofAzimuth = plane.primary ? po.roofAzimuth ?? spacing.site_geometry?.roof?.azimuthDeg
      : plane.roofAzimuth ?? spacing.site_geometry?.roof?.azimuthDeg ?? po.roofAzimuth;
    if (spacing.site_geometry?.roof && String(po.installationType).startsWith("roof")) {
      const flush = po.installationType === "roof_flush";
      spacing = { ...spacing, site_geometry: { ...spacing.site_geometry, roof: { ...spacing.site_geometry.roof,
        pitchDeg: flush ? plane.tilt : roofPitch,
        azimuthDeg: flush ? plane.azim : roofAzimuth } } };
      if (siteGeometryErrors(spacing.site_geometry).length) fail(i, "site_geometry");
    }
    const mounting = mountingPlaneFor({ ...cfg, panelOrientation: { ...po, roofPitch, roofAzimuth,
      tilt: plane.tilt, azim: plane.azim, tiltReference: plane.tiltReference } }, spacing);
    const scene = resolveSceneModel({ latitude: cfg.loc_info?.lat, longitude: cfg.loc_info?.lon,
      totalModules: summaries[i].modules, surfaceTilt: mounting.tilt, surfaceAzimuth: mounting.azimuth,
      pitch: spacing.minimum_distance_m ?? spacing.pitch_m, rowVerticalModules: spacing.row_vertical_modules,
      moduleLength: length, moduleWidth: width, groundClearance: plane.groundClearanceM ?? po.groundClearanceM,
      fieldRows: spacing.field_rows, rowModules: spacing.row_modules, parcelTables: spacing.parcel_tables,
      parcelOutline: spacing.parcel_outline, arrayOffsetEast: spacing.array_offset_east_m, arrayOffsetNorth: spacing.array_offset_north_m,
      slopeTilt: cfg.loc_info?.tilt, slopeAzimuth: cfg.loc_info?.azimuth, portrait: po.moduleOrientation !== "landscape",
      mountType: cfg.mountType, axisOrientation: cfg.axisOrientation, maxAngle: cfg.maxAngle, backTrack: cfg.backTrack,
      installationType: po.installationType, siteGeometry: spacing.site_geometry,
      roofPitch, roofAzimuth });
    if (!scene) {
      if (cfg.loc_info?.lat == null || cfg.loc_info?.lon == null ||
        !Number.isFinite(Number(cfg.loc_info.lat)) || !Number.isFinite(Number(cfg.loc_info.lon))) fail(i, "coordinates");
      fail(i, "site_geometry");
    }
    if (!Number.isFinite(scene.lat)) fail(i, "coordinates");
    if (spacing.module_array) {
      const rows = buildRows(scene, scene.surfaceTilt, scene.parcel ? scene.parcel.tables.length : scene.field?.rowsUsed,
        scene.field?.rowLength, null, scene.field?.rowModules).rows;
      spacing = { ...spacing, module_offsets: resolveRoofModuleOffsets(rows, scene.verticalModules, spacing, scene.terrain.groundZ) };
    }
    return { id: plane.id, label: i + 1, plane, saved, scene, spacing,
      siteGeometry: spacing.site_geometry, tables: spacing.parcel_tables,
      tilt: scene.surfaceTilt, azimuth: scene.surfaceAzimuth, tiers: scene.verticalModules,
      moduleAlongSlope: scene.collectorWidth / scene.verticalModules, moduleWidth: scene.moduleWidth,
      totalModules: summaries[i].modules, clearance: scene.clearance, onRoof: Boolean(scene.roof),
      roofPitch: scene.slopeTilt, roofAzimuth: scene.slopeAzimuth,
      slopeTilt: scene.slopeTilt, slopeAzimuth: scene.slopeAzimuth };
  });
  for (const item of descriptors) {
    cancelled();
    if (item.saved.obstacleShading?.placement) {
      const key = item.plane.primary ? projectId : `${projectId}:orientation:${item.id}`;
      const mesh = await loadMesh(key);
      if (!mesh) throw scenePreparationError("mesh_missing", "Reimport the original 3D model in the layout to calculate its shading.", { planeIndex: item.label });
      item.imported = placeMesh(mesh.triangles, { ...item.saved.obstacleShading.placement, groundZAt: item.scene.terrain.groundZ }).triangles;
    }
  }
  if (detailedShadingEnabled(cfg)) {
    const point = (p) => [p.x, p.y, p.z];
    const planes = descriptors.map((item, i) => {
      cancelled();
      const { scene, spacing } = item;
      const neighbours = buildProjectNeighbours(descriptors.filter((p) => p.id !== item.id), item);
      if (neighbours.missing.length) fail(i, "neighbour_layout");
      const rows = buildRows(scene, scene.surfaceTilt, scene.parcel ? scene.parcel.tables.length : scene.field?.rowsUsed,
        scene.field?.rowLength, null, scene.field?.rowModules).rows;
      const positions = spacing.module_positions || [];
      if (positions.length !== item.totalModules) fail(i, "module_count");
      if (positions.some(p => !rows[p.row - 1])) fail(i, "module_mapping");
      const quads = positions.map((p) => moduleQuad(rows[p.row - 1], p, scene.verticalModules, spacing.module_offsets, scene.terrain.groundZ));
      const roofCheck = checkRoofRows(item.siteGeometry, quads);
      if (roofCheck.outside) fail(i, "outside_roof");
      if (roofCheck.intersecting) fail(i, "obstacle_collision");
      if (roofCheck.belowRoof) fail(i, "below_roof");
      if (overlapCount(quads)) fail(i, "module_overlap");
      const importedNeighbours = descriptors.filter((p) => p.id !== item.id && p.imported)
        .map((p) => translateMesh(p.imported, sceneOffset(item, p)));
      const obstacles = combineTriangles(buildSiteGeometry(item.siteGeometry, scene.terrain.groundZ).obstacles,
        neighbours.triangles, item.imported, ...importedNeighbours);
      onProgress((i + 1) / descriptors.length);
      return { orientationId: item.id, portrait: cfg.panelOrientation?.moduleOrientation !== "landscape",
        modules: positions.map((p, j) => ({ ...p, corners: [quads[j].low[0], quads[j].low[1], quads[j].high[1], quads[j].high[0]].map(point) })),
        triangles: Array.from(obstacles || []) };
    });
    return { ...cfg, pvDetailedShading: { ...cfg.pvDetailedShading, scene: { version: 1,
      key: sceneShadingKey(cfg) + detailedSceneKey(cfg, [length, width]), sourceInputs: detailedSceneInputs(cfg), moduleDimensions: [length, width], planes } } };
  }
  let result = cfg;
  for (const [i, item] of descriptors.entries()) {
    cancelled();
    const { scene, spacing, saved } = item;
    const neighbours = buildProjectNeighbours(descriptors.filter((p) => p.id !== item.id), item);
    if (neighbours.missing.length) fail(i, "neighbour_layout");
    const rowsAt = (rotation) => buildRows(scene, rotation,
      scene.parcel ? scene.parcel.tables.length : scene.field?.rowsUsed,
      scene.field?.rowLength, null, scene.field?.rowModules).rows;
    const rows = rowsAt(scene.surfaceTilt);
    const positions = spacing.module_positions || [];
    if (positions.length !== item.totalModules) fail(i, "module_count");
    if (positions.some(p => !rows[p.row - 1])) fail(i, "module_mapping");
    const quads = moduleQuads(rows, scene.verticalModules, spacing.module_offsets, scene.terrain.groundZ, spacing.excluded_slots);
    const roofCheck = checkRoofRows(item.siteGeometry, quads);
    if (roofCheck.outside) fail(i, "outside_roof");
    if (roofCheck.intersecting) fail(i, "obstacle_collision");
    if (roofCheck.belowRoof) fail(i, "below_roof");
    if (overlapCount(quads)) fail(i, "module_overlap");
    const importedNeighbours = descriptors.filter((p) => p.id !== item.id && p.imported)
      .map((p) => translateMesh(p.imported, sceneOffset(item, p)));
    const obstacles = combineTriangles(buildSiteGeometry(item.siteGeometry, scene.terrain.groundZ).obstacles,
      neighbours.triangles, item.imported, ...importedNeighbours);
    const resolution = (spacing.shading_samples_per_module ?? saved.obstacleShading?.table?.samplesPerModule) === 64 ? 8 : 4;
    const sampleRows = (r) => moduleSamples(r, positions, scene.verticalModules, resolution, spacing.module_offsets, scene.terrain.groundZ);
    const table = await measure({
      triangles: scene.tracker ? obstacles : combineTriangles(obstacles, rowsMesh(quads)),
      rows,
      explicitSamples: sampleRows(rows), rowCount: rows.length, moduleMetadata: positions, includesRows: !scene.tracker,
      ...(scene.tracker ? { samplesFor: (az, el) => sampleRows(rowsAt(trackerRotation({ solarZenith: 90 - el, solarAzimuth: az,
        axisTilt: scene.axisTilt, axisAzimuth: scene.axisAzimuth, maxAngle: scene.maxAngle,
        backtrack: scene.backtrack, gcr: scene.gcr, crossAxisTilt: scene.crossAxisSlope }) ?? 0)) } : {}),
      ...(resolution === 8 ? { azStep: 5, azCount: 72, elStep: 2.5, elCount: 37 } : {}),
      latitude: scene.lat, shouldCancel,
      onProgress: (progress) => onProgress((i + progress) / descriptors.length),
    });
    cancelled();
    if (!table) throw new Error("Scene preparation cancelled.");
    result = saveOrientationShading(result, item.id, { table, sceneVersion: 2, stale: false,
      ...(saved.obstacleShading?.placement ? { placement: saved.obstacleShading.placement } : {}),
      siteGeometry: item.siteGeometry, contextIds: descriptors.filter((p) => p.id !== item.id).map((p) => p.id),
      measuredAt: new Date().toISOString(), source: { name: "Shared project scene", format: "parametric" } });
  }
  return result;
}
